// ===================================================================
// MainStreet — Verify Re-entry Pass (Public Page)
// Route: /mainstreet/verify/:code
// No auth required — fetches passes table directly
// Enhanced with certificate card display, QR code, and official look
// ===================================================================

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Loader, Code,
} from '../../components/gab/GabComponents';
import {
  IconShieldCheck, IconShieldX, IconQuestionMark,
} from '@tabler/icons-react';
import QRCode from 'qrcode';
import { TABLES, FIELDS } from '../../lib/api-switch';
import { fetchRecords } from '../../api/client';
import { primary, semantic } from '../../lib/colors';
import { fmtDate, fmtDateTime } from '../../lib/dateFormat';

// -- Types ----------------------------------------------------------

interface PassRecord {
  id: number;
  authId: string;
  orgId: string;
  orgName: string;
  code: string;
  status: string;
  issuedAt: string;
  expiresAt: string;
  verifiedAt: string;
}

function mapPass(row: Record<string, unknown>): PassRecord {
  const FP = FIELDS.msReentryPasses;
  const id = Number(row.record_id) || Number(row.id) || 0;
  return {
    id,
    authId: String(row[FP.authId] ?? ''),
    orgId: String(row[FP.orgId] ?? ''),
    orgName: String(row[FP.orgName] ?? ''),
    code: String(row[FP.passCode] ?? ''),
    status: String(row[FP.status] ?? ''),
    issuedAt: String(row[FP.issuedAt] ?? ''),
    expiresAt: String(row[FP.expiresAt] ?? ''),
    verifiedAt: String(row[FP.verifiedAt] ?? ''),
  };
}

// ===================================================================
export default function VerifyPass() {
  const { code } = useParams<{ code: string }>();
  const [pass, setPass] = useState<PassRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');

  useEffect(() => {
    if (!code) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    async function lookup() {
      try {
        const upperCode = code!.toUpperCase();
        // V2 API text field eq-filter is unreliable — fetch all passes and filter client-side.
        // There are at most a few hundred passes per incident so this is acceptable.
        const result = await fetchRecords<Record<string, unknown>>(TABLES.msReentryPasses, { limit: 500, anonymous: true });
        const passes = (result.records ?? []).map(mapPass);
        const match = passes.find(p => p.code.toUpperCase() === upperCode);
        if (match) {
          setPass(match);
          // Generate QR code
          try {
            const verifyUrl = `${window.location.origin}/mainstreet/verify/${encodeURIComponent(match.code)}`;
            const dataUrl = await QRCode.toDataURL(verifyUrl, {
              width: 160,
              margin: 2,
              color: { dark: primary[900], light: '#ffffff' },
            });
            setQrDataUrl(dataUrl);
          } catch {
            // QR generation failed silently
          }
        } else {
          console.warn('[VerifyPass] No pass found for code:', upperCode);
          setNotFound(true);
        }
      } catch (err) {
        console.error('[VerifyPass] Failed to fetch passes:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    lookup();
  }, [code]);

  // Determine validity
  const isExpired = pass?.expiresAt ? new Date(pass.expiresAt) < new Date() : false;
  const isRevoked = pass?.status === 'revoked';
  const isValid = pass && !isExpired && !isRevoked;

  // -- Render -------------------------------------------------------

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-800) 50%, var(--color-primary-700) 100%)',
      padding: 24,
    }}>
      <Paper shadow="lg" radius="md" p={0} style={{ maxWidth: 520, width: '100%', overflow: 'hidden' }}>
        {/* Top accent bar */}
        <div style={{
          height: 6,
          background: isValid === null
            ? 'linear-gradient(90deg, var(--color-primary-800), var(--color-primary-700))'
            : isValid
              ? 'linear-gradient(90deg, #07963F, #0ab34e, #07963F)'
              : 'linear-gradient(90deg, var(--color-error), #e84836, var(--color-error))',
        }} />

        <div style={{ padding: '28px 32px 32px' }}>
          {/* OpenGov branding */}
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <div style={{
              fontSize: 12,
              letterSpacing: 3,
              color: primary[800],
              fontWeight: 700,
            }}>
              OPENGOV EMERGENCY MANAGEMENT
            </div>
          </div>
          <Title order={4} ta="center" mb="lg" style={{ color: primary[900] }}>
            Re-entry Pass Verification
          </Title>

          {loading && (
            <Group justify="center" p="xl">
              <Loader size="md" />
              <Text c="dimmed">Verifying pass...</Text>
            </Group>
          )}

          {!loading && notFound && (
            <div style={{ textAlign: 'center', padding: '24px 16px' }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <IconQuestionMark size={40} style={{ color: '#999' }} />
              </div>
              <Title order={4} mb="xs">Pass Not Found</Title>
              <Text size="sm" c="dimmed">
                No re-entry pass matches code{' '}
                <Code style={{ fontFamily: 'DM Mono, monospace' }}>{code}</Code>.
              </Text>
              <Text size="xs" c="dimmed" mt="sm">
                Please verify the code and try again.
              </Text>
            </div>
          )}

          {!loading && pass && isValid && (
            <div>
              {/* Green checkmark circle */}
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #07963F, #0ab34e)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  boxShadow: '0 4px 16px rgba(7, 150, 63, 0.3)',
                }}>
                  <IconShieldCheck size={44} style={{ color: '#fff' }} />
                </div>
              </div>

              <Title order={3} ta="center" mb="md" style={{ color: '#07963F' }}>
                VALID PASS
              </Title>

              {/* Mini certificate card */}
              <div style={{
                border: '2px solid #07963F',
                borderRadius: 4,
                padding: 24,
                background: '#f0fdf4',
              }}>
                {/* QR code */}
                {qrDataUrl && (
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <img
                      src={qrDataUrl}
                      alt="QR Code"
                      style={{ width: 120, height: 120, borderRadius: 4 }}
                    />
                  </div>
                )}

                {/* Pass code */}
                <div style={{
                  textAlign: 'center',
                  marginBottom: 16,
                  padding: '8px 0',
                  background: '#fff',
                  borderRadius: 4,
                  border: '1px solid #d4edda',
                }}>
                  <div style={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>
                    Pass Code
                  </div>
                  <div style={{
                    fontSize: 24,
                    fontWeight: 800,
                    fontFamily: "'DM Mono', monospace",
                    letterSpacing: 3,
                    color: primary[900],
                  }}>
                    {pass.code}
                  </div>
                </div>

                {/* Details */}
                <div style={{ fontSize: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#666', fontWeight: 500 }}>Organization</span>
                    <span style={{ fontWeight: 600 }}>{pass.orgName || '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#666', fontWeight: 500 }}>Status</span>
                    <Badge color="var(--color-success)" size="sm">{pass.status}</Badge>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#666', fontWeight: 500 }}>Issued</span>
                    <span style={{ fontWeight: 500 }}>{fmtDate(pass.issuedAt)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#666', fontWeight: 500 }}>Expires</span>
                    <span style={{ fontWeight: 500 }}>{fmtDate(pass.expiresAt)}</span>
                  </div>
                  {pass.verifiedAt && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666', fontWeight: 500 }}>Previously Verified</span>
                      <span style={{ fontWeight: 500 }}>{fmtDateTime(pass.verifiedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!loading && pass && !isValid && (
            <div>
              {/* Red X circle */}
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--color-error), #e84836)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  boxShadow: '0 4px 16px rgba(211, 52, 35, 0.3)',
                }}>
                  <IconShieldX size={44} style={{ color: '#fff' }} />
                </div>
              </div>

              <Title order={3} ta="center" mb="md" style={{ color: semantic.error }}>
                INVALID PASS
              </Title>

              {/* Mini certificate card (red theme) */}
              <div style={{
                border: '2px solid var(--color-error)',
                borderRadius: 4,
                padding: 24,
                background: '#fef2f2',
              }}>
                {/* Pass code */}
                <div style={{
                  textAlign: 'center',
                  marginBottom: 16,
                  padding: '8px 0',
                  background: '#fff',
                  borderRadius: 4,
                  border: '1px solid #fecaca',
                }}>
                  <div style={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>
                    Pass Code
                  </div>
                  <div style={{
                    fontSize: 24,
                    fontWeight: 800,
                    fontFamily: "'DM Mono', monospace",
                    letterSpacing: 3,
                    color: '#999',
                    textDecoration: 'line-through',
                  }}>
                    {pass.code}
                  </div>
                </div>

                {/* Details */}
                <div style={{ fontSize: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#666', fontWeight: 500 }}>Organization</span>
                    <span style={{ fontWeight: 600 }}>{pass.orgName || '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#666', fontWeight: 500 }}>Status</span>
                    <Badge color="var(--color-error)" size="sm">{isRevoked ? 'Revoked' : 'Expired'}</Badge>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#666', fontWeight: 500 }}>Issued</span>
                    <span style={{ fontWeight: 500 }}>{fmtDate(pass.issuedAt)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#666', fontWeight: 500 }}>Expires</span>
                    <span style={{ fontWeight: 500, color: isExpired ? 'var(--color-error)' : undefined }}>
                      {fmtDate(pass.expiresAt)}
                    </span>
                  </div>
                </div>

                <div style={{
                  textAlign: 'center',
                  marginTop: 16,
                  padding: '8px 16px',
                  background: '#fff',
                  borderRadius: 4,
                  border: '1px solid #fecaca',
                }}>
                  <Text size="xs" c="red.7" fw={500}>
                    This pass is no longer valid. Contact the issuing authority.
                  </Text>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <Text size="xs" c="dimmed" ta="center" mt="lg" style={{ letterSpacing: 0.5 }}>
            OpenGov Emergency Management Center
          </Text>
        </div>
      </Paper>
    </div>
  );
}
